import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { SITE } from '@/data';
import { EASE, MaskedLine } from './Reveal';
import { Mark, PlayDisc } from './Mark';

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const vinylY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const vinylSpin = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -46]);

  // The hero card follows the channel: newest full episode wins, so the
  // player, the guest name and the thumbnail update themselves every drop.
  const [latest, setLatest] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch('/api/youtube/latest')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('feed unavailable'))))
      .then((d) => { if (alive) setLatest(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Feed titles look like "Top of the Funky House feat. Theo Nasa" or
  // "TOP OF THE FUNKY HOUSE FEAT @KatyBOfficial" — pull the guest name out.
  const latestGuest = (latest?.title?.match(/feat\.?\s*(.+)$/i)?.[1]?.replace(/^@/, '') || SITE.latestGuestName).toUpperCase();
  const latestPhoto = latest?.videoId
    ? `https://i.ytimg.com/vi/${latest.videoId}/maxresdefault.jpg`
    : SITE.latestGuestPhoto;

  const playLatest = () => {
    if (SITE.youtubePlaylistId) {
      // Embed loads only on click — fast first paint
      const holder = document.getElementById('latest-video');
      if (holder && !holder.querySelector('iframe')) {
        const f = document.createElement('iframe');
        f.title = 'Top of the Funky House, latest episode';
        f.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        f.allowFullscreen = true;
        f.className = 'absolute inset-0 h-full w-full rounded-3xl border-0';
        const embed = (id) => { f.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0'; };
        if (latest?.videoId) {
          embed(latest.videoId);
        } else {
          // Feed not loaded yet — fetch it, falling back to the playlist embed
          fetch('/api/youtube/latest')
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error('feed unavailable'))))
            .then((d) => embed(d.videoId))
            .catch(() => { f.src = 'https://www.youtube-nocookie.com/embed/videoseries?list=' + encodeURIComponent(SITE.youtubePlaylistId) + '&autoplay=1&rel=0'; });
        }
        holder.appendChild(f);
      }
    } else if (window.__lenis) {
      window.__lenis.scrollTo('#carpool', { offset: -88 });
    }
  };

  return (
    <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16 md:pt-32" data-testid="hero-section">
      {/* ambient glows */}
      <div className="pointer-events-none absolute -right-40 top-1/4 h-[560px] w-[560px] rounded-full bg-gold/10 blur-[140px]" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-mauve/10 blur-[120px]" aria-hidden="true" />

      <div className="relative z-10 mx-auto grid w-full max-w-[1280px] grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:gap-10">
        {/* Copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
            className="mb-7 flex flex-wrap items-center gap-3"
          >
            <span className="rounded-full border border-gold/40 px-4 py-1.5 font-mono text-[12px] font-bold uppercase tracking-[0.22em] text-gold">
              Princess Nyah presents
            </span>
            <span className="rounded-full bg-sky/15 px-4 py-1.5 font-mono text-[12px] font-bold uppercase tracking-[0.22em] text-sky">
              UK Funky House
            </span>
          </motion.div>

          <h1 className="font-display uppercase leading-[0.88] text-ivory" style={{ fontSize: 'clamp(3.5rem, 9.5vw, 8.75rem)', letterSpacing: '0.01em' }} data-testid="hero-title">
            <MaskedLine delay={0.15}>TOP OF THE</MaskedLine>
            <MaskedLine delay={0.28}>
              <span className="inline-block -skew-x-6 text-mauve">FUNKY</span>
            </MaskedLine>
            <MaskedLine delay={0.41}>
              <span className="text-gold">HOUSE</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
            className="mt-7 max-w-[520px] text-lg leading-relaxed text-ivory/75 md:text-xl"
            data-testid="hero-lead"
          >
            Funky house legends get in the car with Princess Nyah, talk through the tunes that
            shaped them and give us their Top 10.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.72 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              type="button"
              onClick={playLatest}
              data-testid="hero-watch-latest-btn"
              className="group inline-flex items-center gap-3 rounded-full bg-gold px-7 py-4 text-base font-extrabold text-plum transition-transform duration-200 hover:scale-[1.04] hover:shadow-[0_0_36px_rgba(232,184,106,0.5)]"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="transition-transform duration-200 group-hover:scale-125">
                <polygon points="7 4 20 12 7 20 7 4" />
              </svg>
              Watch the latest
            </button>
            <a
              href="#guestlist"
              data-testid="hero-guestlist-btn"
              className="inline-flex items-center gap-2 rounded-full border-2 border-ivory/20 px-7 py-4 text-base font-extrabold text-ivory transition-[border-color,background-color] duration-200 hover:border-gold hover:text-gold"
            >
              Get every drop first
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-10 flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.2em] text-ivory/50"
            data-testid="hero-drop-schedule"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gold" />
            </span>
            NEW EPISODES EVERY MONDAY · 6PM GMT
          </motion.div>
        </div>

        {/* Latest episode card + vinyl */}
        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: vinylY }}
            className="pointer-events-none absolute -top-24 -right-16 z-0 hidden opacity-40 md:block lg:-right-28 lg:-top-28"
            aria-hidden="true"
          >
            <motion.div style={{ rotate: vinylSpin }} className="animate-spin-slow">
              <Mark size={400} />
            </motion.div>
          </motion.div>

          <motion.div
            style={{ y: cardY }}
            initial={{ opacity: 0, y: 44, rotate: -3 }}
            animate={{ opacity: 1, y: 0, rotate: -2 }}
            transition={{ duration: 1, ease: EASE, delay: 0.5 }}
            className="group relative z-10 rounded-[28px] border border-ivory/10 bg-plum-card p-3.5 shadow-[0_24px_80px_rgba(0,0,0,0.45)] transition-transform duration-500 hover:rotate-0"
            data-testid="hero-episode-card"
          >
            <div id="latest-video" className="video spotlight relative aspect-video overflow-hidden rounded-3xl bg-plum-deep">
              <img
                src={latestPhoto}
                alt={`Princess Nyah and ${latestGuest} in the car, Top of the Funky House`}
                width="1600"
                height="900"
                onError={(e) => { if (e.currentTarget.src !== SITE.latestGuestPhoto) e.currentTarget.src = SITE.latestGuestPhoto; }}
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                data-testid="hero-episode-photo"
              />
              <span className="absolute left-4 top-4 z-10 flex items-center gap-2 rounded-full border border-ivory/15 bg-plum-deep/80 px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-ivory backdrop-blur-md">
                <span className="h-1.5 w-1.5 rounded-full bg-gold" /> Latest episode
              </span>
              <button
                type="button"
                onClick={playLatest}
                aria-label="Play the latest episode"
                data-testid="hero-episode-play"
                className="absolute inset-0 z-10 flex items-center justify-center"
              >
                <span className="transition-transform duration-300 group-hover:scale-110">
                  <PlayDisc size={78} />
                </span>
              </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-3 px-2 pb-1.5 pt-4">
              <div>
                <div className="font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-sky">In the car now</div>
                <div className="font-display text-3xl leading-tight tracking-wide text-ivory md:text-4xl" data-testid="hero-episode-name">
                  {latestGuest}
                </div>
              </div>
              <a href="#carpool" data-testid="hero-all-episodes-link" className="pb-1 text-[15px] font-bold text-gold transition-colors duration-200 hover:text-mauve">
                All episodes →
              </a>
            </div>
          </motion.div>

          <a
            href="#guestlist"
            data-testid="hero-next-guest-strip"
            className="group mt-4 flex items-center gap-4 rounded-[22px] border border-gold/40 bg-plum-card p-3 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-gold hover:shadow-[0_12px_40px_rgba(232,184,106,0.2)]"
          >
            <img
              src={SITE.nextGuestPhoto}
              alt={`Princess Nyah and ${SITE.nextGuest} — next episode`}
              loading="lazy"
              className="h-[72px] w-[128px] shrink-0 rounded-xl object-cover object-[75%_center]"
            />
            <div className="min-w-0">
              <div className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-gold">Next in the car</div>
              <div className="font-display text-2xl leading-tight tracking-wide text-ivory">{SITE.nextGuest}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-sky">Drops Monday 6PM</div>
            </div>
            <span className="ml-auto mr-2 hidden shrink-0 rounded-full bg-gold px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-plum transition-transform duration-200 group-hover:scale-105 sm:block">
              Be first
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
